import { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stack,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import ResizableDrawer from 'components/Layout/ResizableDrawer';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  item: any;
  contractLookups: any;
}

const ContractDrawer: React.FC<Props> = ({ open, onClose, onSave, item, contractLookups }) => {
  const [form, setForm] = useState({
    startDate: '', dueDate: '', rate: '', ptoQty: '',
    typeId: '' as number | '', statusId: '' as number | '', rateTypeId: '' as number | '',
  });
  const [saving, setSaving] = useState(false);

  const types = contractLookups?.types ?? [];
  const statuses = contractLookups?.statuses ?? [];
  const rateTypes = contractLookups?.rateTypes ?? [];

  useEffect(() => {
    setForm({
      startDate: item?.startDate ? item.startDate.split('T')[0] : '',
      dueDate: item?.dueDate ? item.dueDate.split('T')[0] : '',
      rate: item?.rate?.toString() ?? '',
      ptoQty: item?.ptoQty?.toString() ?? '',
      typeId: item?.typeId ?? item?.contractType?.id ?? item?.type?.id ?? '',
      statusId: item?.statusId ?? item?.contractStatus?.id ?? item?.status?.id ?? '',
      rateTypeId: item?.rateTypeId ?? item?.rateType?.id ?? '',
    });
  }, [item, open]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const setSelect = (key: string) => (e: any) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.startDate || !form.typeId || !form.statusId || !form.rateTypeId) return;
    setSaving(true);
    try {
      await onSave({
        startDate: form.startDate,
        dueDate: form.dueDate || null,
        rate: form.rate ? parseFloat(form.rate) : null,
        ptoQty: form.ptoQty ? parseInt(form.ptoQty) : null,
        typeId: Number(form.typeId),
        statusId: Number(form.statusId),
        rateTypeId: Number(form.rateTypeId),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResizableDrawer open={open} onClose={onClose} defaultWidth={440}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {item ? intl.get('hr.contract.edit') : intl.get('hr.contract.add')}
      </Typography>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <TextField label={intl.get('hr.contract.startDate')} value={form.startDate} onChange={set('startDate')} fullWidth required type="date" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
        <TextField label={intl.get('hr.contract.dueDate')} value={form.dueDate} onChange={set('dueDate')} fullWidth type="date" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{intl.get('hr.contract.type')}</InputLabel>
          <Select value={form.typeId} onChange={setSelect('typeId')} label={intl.get('hr.contract.type')}>
            {types.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.typeName}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{intl.get('hr.contract.status')}</InputLabel>
          <Select value={form.statusId} onChange={setSelect('statusId')} label={intl.get('hr.contract.status')}>
            {statuses.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.statusName}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{intl.get('hr.contract.rateType')}</InputLabel>
          <Select value={form.rateTypeId} onChange={setSelect('rateTypeId')} label={intl.get('hr.contract.rateType')}>
            {rateTypes.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.typeName}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label={intl.get('hr.contract.rate')} value={form.rate} onChange={set('rate')} fullWidth type="number" inputProps={{ min: 0, step: '0.01' }} sx={{ mb: 2 }} />
        <TextField label={intl.get('hr.contract.ptoQty')} value={form.ptoQty} onChange={set('ptoQty')} fullWidth type="number" inputProps={{ min: 0 }} sx={{ mb: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose}>{intl.get('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={saving}>{intl.get('common.save')}</Button>
        </Stack>
      </Box>
    </ResizableDrawer>
  );
};

export default ContractDrawer;
